import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";
import { selectors, actions } from "@shared/store";
import { MODAL_TYPES } from "@shared/constants";
import { ModalSize } from "@shared/interfaces";
import { useDraggable, useResize } from "@shared/hooks";
import {
  Icon,
  ModalAccountPayment,
  ModalCreatePropertyList,
  ModalGenerateFile,
  ModalDownloadCsvPropertyRecords,
  ModalNewPropertyList,
  ModalImagePreview,
} from "@shared/components";
import { ModalDisputeLead } from "@containers/PayPerLead/components/PayPerLeadDashboardContainer";
import { ModalAssignLeadToProperty } from "@shared/components/ModalAssignLeadToProperty/ModalAssignLeadToProperty";
import { ModalMoveUserToAnotherCompany } from "@shared/components/ModalMoveUserToAnotherCompany/ModalMoveUserToAnotherCompany";
import { ModalEditCountyPrice } from "@shared/components/ModalEditCountyPrice/ModalEditCountyPrice";
import { ModalApplyCountyPrice } from "@shared/components/ModalApplyCountyPrice/ModalApplyCountyPrice";
import { ModalCancelPayPerLeadSubs } from "@shared/components/ModalCancelSubscription/ModalCancelPayPerLeadSubs";
import { ModalCancelNicheSubs } from "@shared/components/ModalCancelSubscription/ModalCancelNicheSubs";
import { ModalPPLBuyerProfile } from "@containers/PayPerLead/components/PayPerLeadDashboardContainer/LeadsList/components";
import { ModalAdjustAccountBalance } from "@shared/components/ModalAdjustAccountBalance/ModalAdjustAccountBalance";
import { ModalReisiftIntegration } from "@shared/components/ModalReisiftIntegration/ModalReisiftIntegration";
import { ModalSendFilesToReisift } from "@shared/components/ModalSendFilesToReisift";

import { ModalConfirm } from "../ModalConfirm";

import "./index.scss";

const ModalWrapper: FC = () => {
  const dispatch = useDispatch();
  const dragElementRef = useRef<HTMLDivElement>(null);
  const modalElementRef = useRef<HTMLDivElement>(null);
  const resizerElementRef = useRef<HTMLDivElement>(null);

  const { dragging } = useDraggable(dragElementRef, modalElementRef);
  useResize(resizerElementRef, modalElementRef);

  const modal = useSelector(selectors.getModal());

  const onClose = useCallback(() => {
    if (modal?.props.onClose) {
      modal.props.onClose();
    }

    dispatch(actions.hideModal());
  }, [modal, dispatch]);

  const onSuccess = useCallback(
    (value?: unknown) => {
      if (modal?.props.onSuccess) {
        modal.props.onSuccess(value);
      }

      dispatch(actions.hideModal());
    },
    [modal, dispatch],
  );

  const Component = useMemo(() => {
    if (!modal) return;
    return modalStrategy(modal.type);
  }, [modal]);

  useEffect(() => {
    const original = document.body.style.overflow;
    if (modal) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = original;
    };
  }, [modal]);

  return modal ? (
    <>
      {!modal.draggable && <div className="dynamic-modal-overlay" />}
      <div
        className={classnames("dynamic-modal-wrapper", `${modal.type?.toLowerCase()}`, {
          "dynamic-modal-wrapper_draggable": modal.draggable,
        })}
      >
        <div
          ref={modalElementRef}
          className={classnames(
            "dynamic-modal",
            "box",
            modal.className,
            `dynamic-modal_size-${modal.size || ModalSize.NORMAL}`,
            {
              "dynamic-modal_dragging": dragging,
            },
          )}
        >
          {modal.draggable && (
            <div ref={dragElementRef} className="dynamic-modal-drag-panel">
              <span className="dynamic-modal-drag-panel-dot" />
              <span className="dynamic-modal-drag-panel-dot" />
              <span className="dynamic-modal-drag-panel-dot" />
            </div>
          )}
          <div className="dynamic-modal-content">
            {Component ? <Component {...modal.props} onClose={onClose} onSuccess={onSuccess} /> : null}
          </div>
          {modal.resizable && (
            <div ref={resizerElementRef} className="dynamic-modal-resizer">
              ⟷
            </div>
          )}
          {modal.closeIcon && <Icon type="close" className="modal-close" onClick={onClose} />}
        </div>
      </div>
    </>
  ) : null;
};

export default ModalWrapper;

const modalStrategy = (type: string) => {
  switch (type) {
    case MODAL_TYPES.CONFIRM: {
      return ModalConfirm;
    }
    case MODAL_TYPES.GENERATE_FILE: {
      return ModalGenerateFile;
    }
    case MODAL_TYPES.ACCOUNT_PAYMENT: {
      return ModalAccountPayment;
    }
    case MODAL_TYPES.CREATE_PROPERTY_LIST: {
      return ModalCreatePropertyList;
    }
    case MODAL_TYPES.NEW_PROPERTY_LIST: {
      return ModalNewPropertyList;
    }
    case MODAL_TYPES.DOWNLOAD_CSV_PROPERTY_RECORDS: {
      return ModalDownloadCsvPropertyRecords;
    }
    case MODAL_TYPES.DISPUTE_PAY_PER_LEAD: {
      return ModalDisputeLead;
    }
    case MODAL_TYPES.IMAGE_PREVIEW: {
      return ModalImagePreview;
    }
    case MODAL_TYPES.ASSIGN_LEAD_TO_PROPERTY: {
      return ModalAssignLeadToProperty;
    }
    case MODAL_TYPES.MOVE_USER_TO_ANOTHER_COMPANY: {
      return ModalMoveUserToAnotherCompany;
    }
    case MODAL_TYPES.EDIT_COUNTY_PRICE: {
      return ModalEditCountyPrice;
    }
    case MODAL_TYPES.APPLY_COUNTY_PRICE: {
      return ModalApplyCountyPrice;
    }
    case MODAL_TYPES.CANCEL_PAY_PER_LEAD_SUBS: {
      return ModalCancelPayPerLeadSubs;
    }
    case MODAL_TYPES.CANCEL_NICHE_SUBS: {
      return ModalCancelNicheSubs;
    }
    case MODAL_TYPES.PPL_BUYER_PROFILE: {
      return ModalPPLBuyerProfile;
    }
    case MODAL_TYPES.ADJUST_ACCOUNT_BALANCE: {
      return ModalAdjustAccountBalance;
    }
    case MODAL_TYPES.REISIFT_INTEGRATION: {
      return ModalReisiftIntegration;
    }
    case MODAL_TYPES.SEND_FILES_TO_REISIFT: {
      return ModalSendFilesToReisift;
    }
  }
};
